import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import { X, FileText, Sheet, Loader, CheckCircle2, ChevronDown } from 'lucide-react';
import documentsApi from '../../../api/documentsApi';
import InvoiceConfirmationModal from './InvoiceConfirmationModal';
import { getTagPillClasses, isSameTagLabel, normalizeTagLabel } from '../utils/tagStyles';
import type { UploadDocumentResponse } from '../../../api/types';
import { getUserFriendlyErrorMessage } from '../../../utils/errorMessages';

interface DocumentsUploadModalProps {
    file: File;
    onClose: () => void;
    onSaveComplete: (result: UploadDocumentResponse) => void;
    businessId?: string;
    availableTagLabels?: string[];
    onLabelsChanged?: () => void;
}

// Document type options
const DOCUMENT_TYPES = [
    { value: 'bills', label: 'Bill' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'expenses', label: 'Expense' },
    { value: 'other', label: 'Other' },
];

type InvoiceUploadStage = 'idle' | 'uploading' | 'parsing' | 'extracting' | 'ready' | 'failed';

const STAGE_MESSAGES: Record<InvoiceUploadStage, string> = {
    idle: '',
    uploading: 'Uploading...',
    parsing: 'Parsing PDF...',
    extracting: 'Analyzing with AI...',
    ready: 'Ready for review',
    failed: 'Something went wrong',
};

type DropdownPosition = {
    top?: number;
    bottom?: number;
    left: number;
    width: number;
};

const DocumentsUploadModal: React.FC<DocumentsUploadModalProps> = ({
    file,
    onClose,
    onSaveComplete,
    businessId,
    availableTagLabels = [],
    onLabelsChanged,
}) => {
    // State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showInvoiceConfirmation, setShowInvoiceConfirmation] = useState(false);
    const [invoiceId, setInvoiceId] = useState<string | null>(null);
    const [invoiceData, setInvoiceData] = useState<any>(null);
    const [invoiceUploadStage, setInvoiceUploadStage] = useState<InvoiceUploadStage>('idle');
    const [confirmationDocType, setConfirmationDocType] = useState<'invoice' | 'expenses' | 'bills'>('invoice');
    const [savedUploadResult, setSavedUploadResult] = useState<UploadDocumentResponse | null>(null);

    // Document Type Dropdown State
    const [documentType, setDocumentType] = useState<string>('');
    const [showTypeDropdown, setShowTypeDropdown] = useState(false);
    const typeDropdownRef = useRef<HTMLDivElement>(null);
    const [typeDropdownPosition, setTypeDropdownPosition] = useState<DropdownPosition | null>(null);

    // Tag Input State
    const [tagInput, setTagInput] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [deletingLabel, setDeletingLabel] = useState<string | null>(null);
    const tagInputRef = useRef<HTMLDivElement>(null);

    const getFileIconConfig = (fileName: string) => {
        const extension = fileName.split('.').pop()?.toLowerCase();

        if (['csv', 'xls', 'xlsx'].includes(extension || '')) {
            return {
                Icon: Sheet,
                color: 'text-green-600',
                bg: 'bg-green-50'
            };
        }

        return {
            Icon: FileText,
            color: 'text-red-600',
            bg: 'bg-red-50'
        };
    };

    // Handle clicking outside type dropdown
    useEffect(() => {
        function handleClickOutside(event: globalThis.MouseEvent) {
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
                setShowTypeDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!showTypeDropdown) {
            return;
        }

        const updateTypeDropdownPosition = () => {
            const trigger = typeDropdownRef.current;
            if (!trigger) {
                return;
            }

            const rect = trigger.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const estimatedHeight = Math.min(DOCUMENT_TYPES.length * 44 + 8, 240);
            const spaceBelow = viewportHeight - rect.bottom;
            const openUpward = spaceBelow < estimatedHeight && rect.top > spaceBelow;

            setTypeDropdownPosition({
                left: rect.left,
                width: rect.width,
                top: openUpward ? undefined : rect.bottom + 4,
                bottom: openUpward ? Math.max(viewportHeight - rect.top + 4, 8) : undefined,
            });
        };

        updateTypeDropdownPosition();
        window.addEventListener('resize', updateTypeDropdownPosition);
        window.addEventListener('scroll', updateTypeDropdownPosition, true);

        return () => {
            window.removeEventListener('resize', updateTypeDropdownPosition);
            window.removeEventListener('scroll', updateTypeDropdownPosition, true);
        };
    }, [showTypeDropdown]);

    // Handle clicking outside tag input
    useEffect(() => {
        function handleClickOutside(event: globalThis.MouseEvent) {
            if (tagInputRef.current && !tagInputRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // --- Tag Handling Logic ---

    const handleAddTag = async (tag: string) => {
        const trimmedTag = normalizeTagLabel(tag);
        if (!trimmedTag) return;
        const exists = tags.some((item) => isSameTagLabel(item, trimmedTag));
        if (!exists) {
            setTags((prev) => [...prev, trimmedTag]);
        }
        try {
            await documentsApi.addTagLabel(trimmedTag, businessId);
            onLabelsChanged?.();
        } catch (err) {
            console.error('Failed to add tag label:', err);
        }
        setTagInput('');
        setShowSuggestions(false);
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setTags(tags.filter(tag => !isSameTagLabel(tag, tagToRemove)));
    };

    const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            void handleAddTag(tagInput);
        } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
            setTags(tags.slice(0, -1));
        }
    };

    const handleTagInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setTagInput(e.target.value);
        setShowSuggestions(true);
    };

    const handleDeleteLabel = async (label: string) => {
        if (!label || deletingLabel) return;
        setDeletingLabel(label);
        try {
            await documentsApi.deleteTagLabel(label, businessId);
            setTags((prev) => prev.filter((tag) => !isSameTagLabel(tag, label)));
            onLabelsChanged?.();
        } catch (err: any) {
            alert(err?.message || 'Failed to delete label.');
        } finally {
            setDeletingLabel(null);
        }
    };

    const filteredSuggestions = availableTagLabels.filter((label) =>
        !tags.some((item) => isSameTagLabel(item, label))
        && label.toLowerCase().includes(tagInput.toLowerCase())
    );

    // --- Save Logic ---

    const handleSave = async () => {
        if (!documentType) {
            setError('Please select a document type.');
            return;
        }

        setLoading(true);
        setError(null);
        setInvoiceUploadStage('uploading');
        try {
            const response = await documentsApi.uploadDocument(file, documentType, tags, businessId);
            setSavedUploadResult(response);

            if ((documentType === 'invoice' || documentType === 'expenses' || documentType === 'bills') && response.job_id) {
                onSaveComplete(response);
                onClose();
                return;
            }

            // Legacy/direct response: invoice_id returned synchronously
            if ((documentType === 'invoice' || documentType === 'expenses') && response.invoice_id) {
                setConfirmationDocType(documentType === 'expenses' ? 'expenses' : 'invoice');
                setInvoiceId(response.invoice_id);
                setInvoiceData(response.invoice_data ?? null);
                setShowInvoiceConfirmation(true);
            } else {
                onSaveComplete(response);
            }
        } catch (err: any) {
            console.error(err);
            setError(getUserFriendlyErrorMessage(err, 'Failed to save document.'));
        } finally {
            setLoading(false);
        }
    };

    const handleInvoiceConfirm = () => {
        setShowInvoiceConfirmation(false);
        setInvoiceId(null);
        setInvoiceData(null);
        if (savedUploadResult) {
            onSaveComplete(savedUploadResult);
        }
    };

    const handleInvoiceCancel = async () => {
        if (invoiceId) {
            try {
                if (confirmationDocType === 'bills') {
                    await documentsApi.deleteBill(invoiceId);
                } else if (confirmationDocType === 'expenses') {
                    await documentsApi.deleteExpense(invoiceId);
                } else {
                    await documentsApi.deleteInvoice(invoiceId);
                }
            } catch (err: any) {
                console.error('Failed to delete on cancel:', err);
            }
        }
        setShowInvoiceConfirmation(false);
        setInvoiceId(null);
        setInvoiceData(null);
    };

    // Helper to format bytes
    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(0)) + ' ' + sizes[i];
    };

    const isFormValid = documentType;
    const isDuplicateNotice = String(error || '').toLowerCase().includes('already exists');

    return (
        <>
            {/* Invoice Confirmation Modal */}
            {showInvoiceConfirmation && invoiceId && (
                <InvoiceConfirmationModal
                    documentType={confirmationDocType}
                    documentId={invoiceId}
                    invoiceData={invoiceData}
                    onClose={handleInvoiceCancel}
                    onConfirm={handleInvoiceConfirm}
                />
            )}

            {/* Upload Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-6">
                <div
                    className="bg-white relative flex flex-col shadow-2xl"
                    style={{
                        width: '100%',
                        maxWidth: '800px',
                        minHeight: '500px',
                        borderRadius: '12px',
                        padding: '32px',
                    }}
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>

                    {/* Header */}
                    <h2 className="text-[20px] font-semibold text-gray-900 mb-6">
                        Upload Document
                    </h2>

                    {error && (
                        <div className={`${isDuplicateNotice ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-red-50 text-red-600'} p-3 rounded-lg mb-4 text-sm`}>
                            {error}
                        </div>
                    )}

                    {/* File Status Card - Already Selected */}
                    <div className="w-full border border-gray-200 rounded-lg p-4 flex items-center gap-4 bg-white shadow-sm">
                        {(() => {
                            const { Icon, color, bg } = getFileIconConfig(file.name);
                            return (
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${bg}`}>
                                    <Icon className={`w-6 h-6 ${color}`} />
                                </div>
                            );
                        })()}

                        <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-center mb-1">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {file.name}
                                </p>
                            </div>

                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>{formatSize(file.size)}</span>
                                <span className="flex items-center gap-1 text-green-600 font-medium">
                                    • <CheckCircle2 className="w-3 h-3" /> Selected
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Spacer */}
                    <div className="mt-6 mb-4"></div>

                    {/* Document Type Dropdown */}
                    <div className="mb-6">
                        <label className="block text-[14px] font-medium text-gray-900 mb-2">
                            Document Type <span className="text-red-500">*</span>
                        </label>
                        <div className="relative" ref={typeDropdownRef}>
                            <button
                                onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                                className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors text-left"
                            >
                                <span className={documentType ? 'text-gray-900' : 'text-gray-400'}>
                                    {documentType
                                        ? DOCUMENT_TYPES.find(t => t.value === documentType)?.label
                                        : 'Select document type...'}
                                </span>
                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {showTypeDropdown && (
                                <div
                                    className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] py-1 overflow-y-auto max-h-60"
                                    style={typeDropdownPosition ?? undefined}
                                >
                                    {DOCUMENT_TYPES.map((type) => (
                                        <button
                                            key={type.value}
                                            onClick={() => {
                                                setDocumentType(type.value);
                                                setShowTypeDropdown(false);
                                            }}
                                            className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${documentType === type.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                                                }`}
                                        >
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tag Input */}
                    <div className="mb-6" ref={tagInputRef}>
                        <label className="block text-[14px] font-medium text-gray-900 mb-2">
                            Tags
                        </label>

                        {/* Selected Tags Display */}
                        {tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-sm ${getTagPillClasses(tag)}`}
                                    >
                                        {tag}
                                        <button
                                            onClick={() => handleRemoveTag(tag)}
                                            className="hover:opacity-80"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Tag Input Field */}
                        <input
                            type="text"
                            value={tagInput}
                            onChange={handleTagInputChange}
                            onKeyDown={handleTagInputKeyDown}
                            onFocus={() => setShowSuggestions(true)}
                            placeholder="Type and press Enter to add tags..."
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />

                        {availableTagLabels.length > 0 && (
                            <div className="mt-3">
                                <p className="px-0 py-1 text-xs text-gray-500 mb-1">Saved labels:</p>
                                <div className="flex flex-wrap gap-2">
                                    {availableTagLabels.map((label) => (
                                        <span
                                            key={`saved-${label}`}
                                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs ${getTagPillClasses(label)}`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => void handleAddTag(label)}
                                                className="hover:underline"
                                            >
                                                {label}
                                            </button>
                                            <button
                                                type="button"
                                                disabled={deletingLabel === label}
                                                onClick={() => void handleDeleteLabel(label)}
                                                className="hover:opacity-80 disabled:opacity-50"
                                                title="Delete label"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Invoice/bill progress: brief enqueue feedback before closing */}
                    {loading && (documentType === 'invoice' || documentType === 'expenses' || documentType === 'bills') && invoiceUploadStage !== 'idle' && (
                        <div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                            <div className="flex items-center gap-3">
                                <Loader className="w-5 h-5 animate-spin text-blue-600 shrink-0" />
                                <div>
                                    <p className="font-medium text-blue-900">
                                        {STAGE_MESSAGES[invoiceUploadStage] || invoiceUploadStage}
                                    </p>
                                    <p className="text-sm text-blue-700 mt-0.5">
                                        {invoiceUploadStage === 'uploading' && 'File received. Starting...'}
                                        {invoiceUploadStage === 'parsing' && 'Queueing document processing in the background.'}
                                        {invoiceUploadStage === 'extracting' && 'Identifying vendor, amount, and line items.'}
                                        {invoiceUploadStage === 'ready' && 'Done.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Footer: Buttons */}
                    <div className="mt-auto flex justify-end gap-3 pt-4">
                        <button
                            onClick={onClose}
                            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium py-2.5 px-6 rounded-lg text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={loading || !isFormValid}
                            className={`font-medium py-2.5 px-8 rounded-lg text-sm flex items-center gap-2 transition-colors ${isFormValid
                                ? 'bg-gray-900 text-white hover:bg-gray-800'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            {loading && <Loader className="w-4 h-4 animate-spin" />}
                            Upload
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DocumentsUploadModal;
